/**
 * HeatmapTracker — Captures user behavior on all public pages
 * Tracks: clicks, scroll depth, section visibility time, mouse zones, page exits
 * Batches events and sends to /api/heatmap/events every 8 seconds
 */
import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const BATCH_INTERVAL = 8000; // Send batch every 8 seconds
const SCROLL_THROTTLE = 1000;

// Skip tracking on admin/internal pages
const EXCLUDED_PREFIXES = ['/admin', '/agency-dashboard', '/affiliate-dashboard'];

function generateSessionId() {
  let sid = sessionStorage.getItem('hm_sid');
  if (!sid) {
    sid = `hm_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    sessionStorage.setItem('hm_sid', sid);
  }
  return sid;
}

export default function HeatmapTracker() {
  const location = useLocation();
  const eventsRef = useRef([]);
  const sessionIdRef = useRef(generateSessionId());
  const pageStartRef = useRef(Date.now());
  const maxScrollRef = useRef(0);
  const sectionTimersRef = useRef({});
  const batchTimerRef = useRef(null);
  const lastScrollRef = useRef(0);

  const isExcluded = EXCLUDED_PREFIXES.some(p => location.pathname.startsWith(p));

  const pushEvent = useCallback((type, data) => {
    eventsRef.current.push({
      type,
      timestamp: Date.now(),
      ...data,
    });
  }, []);

  const flushEvents = useCallback(() => {
    if (eventsRef.current.length === 0) return;

    const batch = {
      session_id: sessionIdRef.current,
      page: location.pathname,
      viewport: { width: window.innerWidth, height: window.innerHeight },
      events: [...eventsRef.current],
      referrer: document.referrer,
    };
    eventsRef.current = [];

    // Use sendBeacon for reliability (especially on page exit)
    const blob = new Blob([JSON.stringify(batch)], { type: 'application/json' });
    const sent = navigator.sendBeacon(`${API}/api/heatmap/events`, blob);
    if (!sent) {
      // Fallback to fetch
      fetch(`${API}/api/heatmap/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(batch),
        keepalive: true,
      }).catch(() => {});
    }
  }, [location.pathname]);

  useEffect(() => {
    if (isExcluded) return;

    // Reset state for new page
    pageStartRef.current = Date.now();
    maxScrollRef.current = 0;
    eventsRef.current = [];
    sectionTimersRef.current = {};

    // ── Click Tracking ──
    const handleClick = (e) => {
      const rect = document.documentElement.getBoundingClientRect();
      const pageHeight = document.documentElement.scrollHeight;
      const pageWidth = document.documentElement.scrollWidth;

      // Get element info
      const el = e.target.closest('a, button, [role="button"], input, [data-testid]') || e.target;
      const tag = el.tagName?.toLowerCase() || '';
      const text = (el.textContent || '').trim().slice(0, 60);
      const testId = el.getAttribute('data-testid') || '';
      // el.className is an SVGAnimatedString (not a string) for SVG elements,
      // which has no .split — guard against it to avoid throwing on icon clicks.
      const cls = typeof el.className === 'string' ? el.className : '';

      pushEvent('click', {
        x_pct: Math.round((e.pageX / pageWidth) * 100 * 10) / 10,
        y_pct: Math.round((e.pageY / pageHeight) * 100 * 10) / 10,
        element: testId || `${tag}${cls ? '.' + cls.split(' ')[0] : ''}`,
        text,
      });
    };

    // ── Scroll Depth Tracking ──
    const handleScroll = () => {
      const now = Date.now();
      if (now - lastScrollRef.current < SCROLL_THROTTLE) return;
      lastScrollRef.current = now;

      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const depth = docHeight > 0 ? Math.round((scrollTop / docHeight) * 100) : 0;

      if (depth > maxScrollRef.current) {
        maxScrollRef.current = depth;
        // Record milestone scroll depths
        const milestones = [25, 50, 75, 90, 100];
        for (const m of milestones) {
          if (depth >= m && (maxScrollRef.current - (depth - maxScrollRef.current)) < m) {
            pushEvent('scroll', { depth: m, milestone: true });
          }
        }
      }
      pushEvent('scroll', { depth });
    };

    // ── Section Visibility Tracking (IntersectionObserver) ──
    let observer = null;
    const observeSections = () => {
      const sections = document.querySelectorAll('section[id], [data-section], [data-testid]');
      if (sections.length === 0) return;

      observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          const id = entry.target.id || entry.target.dataset.section || entry.target.dataset.testid || 'unnamed';
          if (entry.isIntersecting) {
            sectionTimersRef.current[id] = Date.now();
          } else if (sectionTimersRef.current[id]) {
            const timeVisible = Math.round((Date.now() - sectionTimersRef.current[id]) / 1000);
            if (timeVisible > 1) {
              pushEvent('section_view', { section_id: id, time_visible: timeVisible });
            }
            delete sectionTimersRef.current[id];
          }
        });
      }, { threshold: 0.3 });

      sections.forEach(s => observer.observe(s));
    };

    // ── Page Exit Tracking ──
    const handleBeforeUnload = () => {
      const timeOnPage = Math.round((Date.now() - pageStartRef.current) / 1000);
      pushEvent('page_exit', {
        time_on_page: timeOnPage,
        max_scroll_depth: maxScrollRef.current,
        scroll_position: maxScrollRef.current,
      });
      flushEvents();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        handleBeforeUnload();
      }
    };

    // Attach listeners
    document.addEventListener('click', handleClick, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Start section observer after DOM settles
    const sectionTimeout = setTimeout(observeSections, 2000);

    // Periodic batch flush
    batchTimerRef.current = setInterval(flushEvents, BATCH_INTERVAL);

    return () => {
      document.removeEventListener('click', handleClick);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearTimeout(sectionTimeout);
      clearInterval(batchTimerRef.current);
      if (observer) observer.disconnect();
      flushEvents(); // Flush remaining events
    };
  }, [location.pathname, isExcluded, pushEvent, flushEvents]);

  return null; // Invisible component
}
